/**
 * Schichtenschnitt eines Rasterfeldes: Belastung ueber die Tiefe, Aushubsohle,
 * Trennlage, Abdeckung — und der Punkt, ab dem Weissbereich gilt.
 *
 * Reine Funktion, kein DOM, kein jsPDF.
 *
 * ═══ WARUM DIESE SKIZZE GEBRAUCHT WIRD ════════════════════════════════════
 * Die Aushubstrategie ist der Teil des A+S-Plans, den auf der Baustelle
 * tatsaechlich jemand befolgen muss, und sie ist in Worten schwer zu fassen:
 * „Aushub bis 0,9 m unter GOK mit SK2, danach 25 cm in unbelastetem Material,
 * anschliessend Trennlage und Sandverfuellung — ab dann SK1 fuer alle
 * Folgegewerke." Ein Baggerfahrer liest das einmal. Ein Schnitt zeigt es.
 *
 * Vor allem zeigt er die Stelle, an der die Schutzklasse WECHSELT — und
 * damit die einzige Stelle, an der ein Fehler unmittelbar Folgen hat.
 *
 * ═══ WAS DIE SKIZZE NICHT TUT ═════════════════════════════════════════════
 * Sie erfindet keine Schichten. Gezeichnet wird, was an Proben erfasst ist;
 * Bereiche ohne Probe erscheinen als ausdrueckliche Luecke („nicht beprobt")
 * und nicht als unbelasteter Boden. Ein weisser Streifen, der in Wahrheit
 * „unbekannt" heisst, waere die gefaehrlichste Aussage dieser ganzen
 * Zeichnung.
 */

import type { Probe } from '../types';
import { ebvKlasse } from '../kataloge/ebvKlassen';
import { klassenLabel } from '../einstufung';
import type { Primitive, Zeichnung } from './primitive';

export interface SchnittParameter {
    feld_nr: string;
    /** Tiefe der geplanten Aushubsohle in m unter GOK. */
    aushubsohle_m: number | null;
    /**
     * Ueberaushub unter der Sohle in m (im Musterplan 0,25 m).
     *
     * `null` heisst „nicht entschieden" und ist NICHT dasselbe wie 0: eine
     * Null sagt aus, dass nicht ueberbaggert wird. Beides wird verschieden
     * gezeichnet. Gleiches gilt fuer `trennlage`, `abdeckung_m` und
     * `weissbereich_danach`.
     */
    ueberaushub_m: number | null;
    /** Wird nach dem Aushub eine Trennlage mit Abdeckung eingebaut? */
    trennlage: boolean | null;
    /** Maechtigkeit der Sandabdeckung in m. */
    abdeckung_m: number | null;
    /** Schutzklasse waehrend des Aushubs bzw. danach — nur zur Beschriftung. */
    schutzklasse_aushub: string;
    schutzklasse_danach: string;
    /**
     * Ob nach dem Aushub ueberhaupt ein Weissbereich entsteht.
     *
     * Nicht selbstverstaendlich: wo nach dem Aushub geflutet wird, gibt es
     * keine Folgegewerke und keinen Wechsel — die Skizze darf dann keinen
     * versprechen.
     *
     * `null` heisst „noch nicht entschieden". Auch das wird ausgewiesen: ein
     * unentschiedener Wechsel als „kein Wechsel" zu drucken waere eine
     * Behauptung, die niemand aufgestellt hat.
     */
    weissbereich_danach: boolean | null;
}

/** Millimeter je Meter Tiefe. */
const MM_JE_M = 26;
const RAND = 14;
const SPALTE_B = 54;

export const zeichneSchnitt = (p: SchnittParameter, proben: Probe[]): Zeichnung => {
    const teile: Primitive[] = [];

    // Nur Proben mit Tiefenangabe koennen gezeichnet werden.
    const mitTiefe = proben
        .filter(pr => pr.tiefe_von !== null && pr.tiefe_bis !== null)
        .sort((a, b) => (a.tiefe_von as number) - (b.tiefe_von as number));
    const ohneTiefe = proben.length - mitTiefe.length;

    const tiefsteProbe = mitTiefe.length > 0
        ? (mitTiefe[mitTiefe.length - 1].tiefe_bis as number)
        : 0;
    const sohle = p.aushubsohle_m ?? 0;
    // Nicht gesetzte Masse gehen als 0 in die GEOMETRIE ein — gezeichnet und
    // beschriftet werden sie deshalb trotzdem nicht (siehe unten).
    const ueberaushub = p.ueberaushub_m ?? 0;
    const abdeckung = p.abdeckung_m ?? 0;
    const unterkante = Math.max(
        tiefsteProbe,
        sohle + ueberaushub,
        1,
    ) + 0.4;

    const x0 = RAND + 16;                 // Platz fuer die Tiefenachse
    const yGok = RAND + 10;
    const yVon = (m: number) => yGok + m * MM_JE_M;

    // ── Gelaendeoberkante ──────────────────────────────────────────────────
    teile.push({
        art: 'linie', x1: x0 - 6, y1: yGok, x2: x0 + SPALTE_B + 8, y2: yGok,
        linie: 'linie_stark', staerke: 0.8,
    });
    teile.push({
        art: 'text', x: x0 - 8, y: yGok - 1.5, text: 'GOK',
        groesse: 6.5, anker: 'rechts', farbe: 'text',
    });

    // ── Schichten ──────────────────────────────────────────────────────────
    let letztesEnde = 0;
    for (const pr of mitTiefe) {
        const von = pr.tiefe_von as number;
        const bis = pr.tiefe_bis as number;

        // Luecke zwischen zwei Proben sichtbar machen.
        if (von > letztesEnde + 0.001) {
            teile.push(...luecke(x0, yVon(letztesEnde), SPALTE_B, (von - letztesEnde) * MM_JE_M));
        }

        const k = ebvKlasse(pr.einstufung);
        teile.push({
            art: 'rechteck', x: x0, y: yVon(von), b: SPALTE_B, h: (bis - von) * MM_JE_M,
            fuellung: k?.farbe ?? '#e2e8f0', linie: 'linie',
        });
        teile.push({
            art: 'text', x: x0 + SPALTE_B / 2, y: yVon(von) + ((bis - von) * MM_JE_M) / 2 + 1,
            text: `${pr.mischprobe || 'Probe'} · ${klassenLabel(pr.einstufung, pr.vorbehalt)}`,
            groesse: 6.5, fett: true, anker: 'mitte', farbe: 'text',
        });
        teile.push({
            art: 'text', x: x0 + SPALTE_B + 4, y: yVon(bis) - 0.5,
            text: `${meter(bis)} m`, groesse: 6, anker: 'links', farbe: 'linie',
        });
        letztesEnde = bis;
    }

    // Unterhalb der tiefsten Probe: unbekannt, nicht unbelastet.
    if (unterkante > letztesEnde) {
        teile.push(...luecke(x0, yVon(letztesEnde), SPALTE_B, (unterkante - letztesEnde) * MM_JE_M));
    }

    // ── Aushubsohle und Ueberaushub ────────────────────────────────────────
    if (p.aushubsohle_m !== null) {
        teile.push({
            art: 'linie', x1: x0 - 4, y1: yVon(sohle), x2: x0 + SPALTE_B + 26, y2: yVon(sohle),
            linie: 'hinweis', staerke: 0.7,
        });
        teile.push({
            art: 'text', x: x0 + SPALTE_B + 28, y: yVon(sohle) + 1,
            text: `geplante Aushubsohle ${meter(sohle)} m`,
            groesse: 6.5, anker: 'links', farbe: 'hinweis',
        });

        if (p.ueberaushub_m !== null && p.ueberaushub_m > 0) {
            const uk = sohle + p.ueberaushub_m;
            teile.push({
                art: 'linie', x1: x0 - 4, y1: yVon(uk), x2: x0 + SPALTE_B + 26, y2: yVon(uk),
                linie: 'hinweis', staerke: 0.5, strich: 'gestrichelt',
            });
            teile.push({
                art: 'text', x: x0 + SPALTE_B + 28, y: yVon(uk) + 1,
                text: `+ ${meter(p.ueberaushub_m)} m Überaushub → ${meter(uk)} m`,
                groesse: 6.5, anker: 'links', farbe: 'hinweis',
            });
        }
    }

    // ── Trennlage und Abdeckung ────────────────────────────────────────────
    let yUnten = yVon(unterkante);
    if (p.trennlage === true && p.aushubsohle_m !== null) {
        const yT = yVon(sohle + ueberaushub);
        teile.push({
            art: 'rechteck', x: x0, y: yT, b: SPALTE_B, h: 3,
            fuellung: 'warnung', linie: 'warnung',
        });
        teile.push({
            art: 'text', x: x0 + SPALTE_B + 28, y: yT + 4,
            text: 'Trennlage (Vlies + Folie)', groesse: 6.5, anker: 'links', farbe: 'warnung',
        });

        if (abdeckung > 0) {
            const hA = abdeckung * MM_JE_M;
            teile.push({
                art: 'rechteck', x: x0, y: yT + 3, b: SPALTE_B, h: hA,
                fuellung: '#fef3c7', linie: 'linie',
            });
            teile.push({
                art: 'text', x: x0 + SPALTE_B / 2, y: yT + 3 + hA / 2 + 1,
                text: `unbelasteter Sand ${meter(abdeckung)} m`,
                groesse: 6, anker: 'mitte', farbe: 'text',
            });
            yUnten = Math.max(yUnten, yT + 3 + hA);
        }
    }

    // ── Schutzklassenwechsel ───────────────────────────────────────────────
    const ySk = yUnten + 8;
    teile.push({
        art: 'text', x: x0, y: ySk,
        text: `während des Aushubs: ${p.schutzklasse_aushub || 'nicht festgelegt'}`,
        groesse: 7, fett: true, anker: 'links', farbe: 'text',
    });

    if (p.weissbereich_danach === true) {
        teile.push({
            art: 'text', x: x0, y: ySk + 5,
            text: `nach Trennlage und Abdeckung: ${p.schutzklasse_danach || 'nicht festgelegt'} (Weißbereich)`,
            groesse: 7, fett: true, anker: 'links', farbe: 'text',
        });
    } else if (p.weissbereich_danach === false) {
        teile.push({
            art: 'text', x: x0, y: ySk + 5,
            text: 'Kein Wechsel in den Weißbereich. Die Schutzklasse gilt bis zum Abschluss.',
            groesse: 7, fett: true, anker: 'links', farbe: 'warnung',
        });
    } else {
        // Weder ja noch nein: das ist ein dritter Zustand und wird als solcher
        // gedruckt. „Kein Wechsel" waere hier eine Entscheidung, die niemand
        // getroffen hat.
        teile.push({
            art: 'text', x: x0, y: ySk + 5,
            text: 'OFFEN: Ob nach dem Aushub ein Weißbereich entsteht, ist nicht entschieden.',
            groesse: 7, fett: true, anker: 'links', farbe: 'warnung',
        });
    }

    const fussnoten: string[] = [];
    if (ohneTiefe > 0) {
        fussnoten.push(`${ohneTiefe} Probe(n) ohne Tiefenangabe sind nicht dargestellt.`);
    }
    fussnoten.push('Schraffierte Bereiche sind nicht beprobt; sie sind nicht als unbelastet '
        + 'zu lesen. Vor Erreichen der Sohle ist vorlaufend zu sondieren.');
    if (p.aushubsohle_m === null) {
        // Ohne Sohle zeigt das Blatt nur die Befundlage. Das muss dranstehen,
        // sonst liest es sich als „es wird nicht ausgehoben".
        fussnoten.push('Für den zugehörigen Arbeitsbereich ist keine Aushubsohle erfasst. '
            + 'Die Zeichnung zeigt die Befundlage, nicht die Aushubstrategie. Aushubsohle, '
            + 'Überaushub, Trennlage und Abdeckung werden am Arbeitsbereich gepflegt.');
    } else if (p.trennlage === null) {
        fussnoten.push('Ob eine Trennlage mit Abdeckung eingebaut wird, ist für diesen '
            + 'Arbeitsbereich nicht entschieden; die Zeichnung stellt sie deshalb nicht dar.');
    }
    if (p.weissbereich_danach !== true) {
        fussnoten.push('Eine Freigabe als Weißbereich setzt den dokumentierten Nachweis '
            + 'voraus, dass an Sohle und Böschung kein belastetes Material mehr ansteht.');
    }

    return {
        breite: x0 + SPALTE_B + 96,
        hoehe: ySk + 12 + RAND,
        titel: `Schichtenschnitt Feld ${p.feld_nr}`,
        untertitel: 'Belastung über die Tiefe, Aushubstrategie und Schutzklassenwechsel',
        teile,
        fussnoten,
    };
};

/** Ein nicht beprobter Abschnitt: schraffiert und beschriftet. */
const luecke = (x: number, y: number, b: number, h: number): Primitive[] => {
    if (h < 1) return [];
    const teile: Primitive[] = [{
        art: 'rechteck', x, y, b, h,
        fuellung: 'flaeche_hell', linie: 'linie', strich: 'gestrichelt',
    }];
    if (h >= 6) {
        teile.push({
            art: 'text', x: x + b / 2, y: y + h / 2 + 1, text: 'nicht beprobt',
            groesse: 6, kursiv: true, anker: 'mitte', farbe: 'linie',
        });
    }
    return teile;
};

const meter = (n: number): string =>
    (Number.isInteger(n) ? n.toFixed(1) : n.toFixed(2)).replace('.', ',');
